<template>
  <div
    :class="[
      $style.toast,
      $style[props.emphasis],
      $style[props.intent],
      { [$style.hasIcon]: props.intent !== 'none', [$style.isCloseable]: props.closeable },
    ]"
  >
    <div :class="$style.content"><SCASvgIcon v-if="icon" :class="$style.icon" :icon="icon" /> {{ text }}</div>
    <div v-if="$slots.default" :class="$style.actions">
      <slot :button-props="btnProps" />
    </div>
    <SCAIconButton
      v-if="closeable"
      type="bare"
      :appearance="closeBtnAppearance"
      icon="phosphor/x"
      @click="snack.close"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useSnack } from "vue-haystack";

import SCAIconButton from "./SCAIconButton.vue";
import SCASvgIcon from "./SCASvgIcon.vue";

type Intent = "none" | "info" | "success" | "warning" | "error";

const props = withDefaults(
  defineProps<{
    text: string;
    intent?: Intent;
    emphasis?: "low" | "high";
    closeable?: boolean;
  }>(),
  {
    intent: "none",
    emphasis: "low",
    closeable: false,
  },
);

const snack = useSnack();

const closeBtnAppearance = computed(() => {
  if (props.emphasis !== "high") {
    return "secondary";
  }
  return "inverted";
});

const btnProps = computed(() => {
  if (props.emphasis !== "high") {
    return {
      appearance: "primary",
      type: "bare",
    };
  }
  if (props.intent === "warning") {
    return {
      appearance: "reverseInverted",
      type: "bare",
    };
  }
  return {
    appearance: "inverted",
    type: "bare",
  };
});

const icon = computed(() => {
  switch (props.intent) {
    case "none":
      return undefined;
    case "info":
      return "phosphor/check-circle-fill";
    case "success":
      return "phosphor/check-circle-fill";
    case "warning":
      return "phosphor/warning-fill";
    case "error":
      return "phosphor/warning-diamond-fill";
    default:
      return "phosphor/info";
  }
});
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.toast {
  @include IconMixins.icon-color(currentColor);
  padding-left: var(--space-component-padding-large);
  padding-right: var(--space-component-padding-large);
  min-height: var(--size-large);
  border-radius: var(--border-radius-large);
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-large);

  box-shadow:
    0px 2px 2px -1px rgba(27, 36, 44, 0.04),
    0px 2px 8px -1px rgba(27, 36, 44, 0.08);
  @include FigmaTypoMixins.typo-body-medium;

  --intent-color: black;

  .content {
    display: flex;
    align-items: center;
    gap: var(--space-component-gap-small);
  }

  .actions {
    display: flex;
    gap: var(--space-component-gap-small);
  }

  &.info {
    --intent-color: var(--color-background-informative-strong);
  }
  &.success {
    --intent-color: var(--color-background-positive-strong);
  }
  &.warning {
    --intent-color: var(--color-background-warning-strong);
  }
  &.error {
    --intent-color: var(--color-background-negative-strong);
  }

  &.hasIcon {
    padding-left: var(--space-component-padding-medium);
  }
  &.isCloseable {
    padding-right: var(--space-component-padding-small);
  }

  &.low {
    border: 1px solid var(--color-border-defaultA);
    background: var(--color-interaction-background-modeless);
    color: var(--color-content-primary);

    .icon {
      @include IconMixins.icon-color(var(--intent-color));
    }
  }

  &.high {
    background-color: var(--intent-color);
    color: var(--color-content-primary-inverted);
  }
}
</style>
